import { useCallback, useEffect, useState } from "react";
import { buildApiUrl } from "@/config/api";
import userStore from "@/store/userStore";

/**
 * Small hook that wraps the Web Push API + our /webpush endpoints:
 *   - checks whether the browser + user is currently subscribed
 *   - subscribes / unsubscribes on demand
 *   - fires a test push after subscribing so the user knows it works
 *
 * Guarded on multiple levels: no service worker → unsupported; no
 * Notification API → unsupported; permission denied → returns 'denied'.
 */
export type PushState =
	| { supported: false; reason: string }
	| { supported: true; permission: NotificationPermission; subscribed: boolean };

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
	const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
	const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
	const raw = atob(base64);
	const buf = new ArrayBuffer(raw.length);
	const arr = new Uint8Array(buf);
	for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
	return arr;
}

const authHeader = () => {
	const t = userStore.getState().userToken?.accessToken;
	return { Authorization: `Bearer ${t}`, "Content-Type": "application/json", Accept: "application/json" };
};

export function usePushSubscription() {
	const [state, setState] = useState<PushState>(() => {
		if (typeof window === "undefined") return { supported: false, reason: "no-window" };
		if (!("serviceWorker" in navigator)) return { supported: false, reason: "no-service-worker" };
		if (!("PushManager" in window)) return { supported: false, reason: "no-push-manager" };
		if (!("Notification" in window)) return { supported: false, reason: "no-notification" };
		return { supported: true, permission: Notification.permission, subscribed: false };
	});
	const [busy, setBusy] = useState(false);

	// Check current subscription on mount
	useEffect(() => {
		if (!state.supported) return;
		(async () => {
			const reg = await navigator.serviceWorker.ready;
			const sub = await reg.pushManager.getSubscription();
			setState((s) => (s.supported ? { ...s, subscribed: !!sub, permission: Notification.permission } : s));
		})();
	}, [state.supported]);

	const subscribe = useCallback(async () => {
		if (!state.supported) return { ok: false, msg: "Ez a böngésző nem támogatja a push értesítéseket" };
		setBusy(true);
		try {
			const perm = await Notification.requestPermission();
			if (perm !== "granted") {
				setState((s) => (s.supported ? { ...s, permission: perm } : s));
				return { ok: false, msg: "Az értesítések engedélye megtagadva" };
			}
			// Fetch VAPID public key
			const keyRes = await fetch(buildApiUrl("/webpush/vapid-public-key"), { headers: authHeader() });
			const { key } = await keyRes.json();
			if (!key) return { ok: false, msg: "Nincs beállított VAPID kulcs a szerveren" };

			const reg = await navigator.serviceWorker.ready;
			const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(key) });
			const raw = sub.toJSON();

			const res = await fetch(buildApiUrl("/webpush/subscribe"), {
				method: "POST",
				headers: authHeader(),
				body: JSON.stringify({ endpoint: sub.endpoint, keys: raw.keys, expirationTime: raw.expirationTime }),
			});
			if (!res.ok) return { ok: false, msg: "Nem sikerült elmenteni a feliratkozást" };
			setState((s) => (s.supported ? { ...s, subscribed: true, permission: perm } : s));
			return { ok: true, msg: "Feliratkozva — küldjünk egy tesztet is?" };
		} finally {
			setBusy(false);
		}
	}, [state.supported]);

	const unsubscribe = useCallback(async () => {
		if (!state.supported) return { ok: false, msg: "Nem támogatott" };
		setBusy(true);
		try {
			const reg = await navigator.serviceWorker.ready;
			const sub = await reg.pushManager.getSubscription();
			if (sub) {
				await fetch(buildApiUrl("/webpush/unsubscribe"), {
					method: "POST",
					headers: authHeader(),
					body: JSON.stringify({ endpoint: sub.endpoint }),
				});
				await sub.unsubscribe();
			}
			setState((s) => (s.supported ? { ...s, subscribed: false } : s));
			return { ok: true, msg: "Leiratkozva" };
		} finally {
			setBusy(false);
		}
	}, [state.supported]);

	const sendTest = useCallback(async () => {
		if (!state.supported) return { ok: false, msg: "Nem támogatott" };
		setBusy(true);
		try {
			const res = await fetch(buildApiUrl("/webpush/test"), { method: "POST", headers: authHeader() });
			const j = await res.json();
			return { ok: res.ok, msg: j.message || (res.ok ? "Elküldve" : "Sikertelen") };
		} finally { setBusy(false); }
	}, [state.supported]);

	return { state, busy, subscribe, unsubscribe, sendTest };
}
